import Tool, {
  type ToolArgsWithController,
  type ToolArgs,
} from '../../../tools/Tool';
import { Editor, Transforms, type Path, Range, Node } from 'slate';
import { getTableStructureFromSelection } from '../TableStructure';
import TableUtils from '../TableUtils';
import mergeCellsSvg from '../../../assets/icons/mergeCells.svg';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';

// Merge the cells in the current table selection.
class ToolMergeCells extends Tool {
  static toolName = 'Merge cells';
  static shortcut = 'Ctrl+Cmd+m';

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(mergeCellsSvg);
  }

  static action({ editor, controller }: ToolArgsWithController): void {
    if (!editor?.selection) return;

    const { tableSelection } = controller.state.pluginState;
    if (!tableSelection) return;

    if (!tableSelection.tableRange || !tableSelection.multipleCellsSelected) {
      return;
    }

    const tablePath = TableUtils.currentTablePath(editor);
    if (!tablePath) return;

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return;

    const { startPosition, endPosition } = tableSelection.tableRange;

    const pathsToRemove: Path[] = [];
    const selectionOriginCell = tableStructure.cell(
      startPosition.columnIndex,
      startPosition.rowIndex
    );
    if (!selectionOriginCell) return;

    const selectionOriginCellPath = [
      ...tablePath,
      ...selectionOriginCell.relativePath,
    ];

    const selectionEndCell = tableStructure.cell(
      endPosition.columnIndex,
      endPosition.rowIndex
    );
    if (!selectionEndCell) return;

    Editor.withoutNormalizing(editor, () => {
      // Make the origin cell wider and higher.
      const newRowSpan =
        endPosition.rowIndex +
        (selectionEndCell.tableCell.table_rowspan ?? 1) -
        startPosition.rowIndex;
      const newColSpan =
        endPosition.columnIndex +
        (selectionEndCell.tableCell.table_colspan ?? 1) -
        startPosition.columnIndex;

      Transforms.setNodes(
        editor,
        {
          table_rowspan: Math.max(1, newRowSpan),
          table_colspan: Math.max(1, newColSpan),
        },
        {
          at: selectionOriginCellPath,
        }
      );

      // Move the content of the other cells into the origin cell.
      let movedNodes = 0;
      for (const path of tableSelection.selectedCellPaths) {
        const coords = tableStructure.pathToCoordinates(path);
        const cellInfo = tableStructure.cell(
          coords.columnIndex,
          coords.rowIndex
        );
        if (!coords || !cellInfo) continue;
        const cellPath = [...tablePath, ...cellInfo.relativePath];
        if (
          startPosition.columnIndex === coords.columnIndex &&
          startPosition.rowIndex === coords.rowIndex
        ) {
          continue;
        }
        if (cellInfo.isActual) {
          if (!TableUtils.cellIsEmpty(editor, cellPath)) {
            const cellChildren = Array.from(
              Node.children(editor, cellPath, {
                reverse: true,
              })
            );

            for (const [, childPath] of cellChildren) {
              Transforms.moveNodes(editor, {
                at: childPath,
                to: [
                  ...selectionOriginCellPath,
                  selectionOriginCell.tableCell.children.length + movedNodes,
                ],
              });
            }

            movedNodes += cellChildren.length;
          }

          // Remember this cell's path to remove it later.
          pathsToRemove.unshift(cellPath); // Unshift to remove the cells in reverse order.
        }
      }

      pathsToRemove.forEach((path) => {
        Transforms.removeNodes(editor, { at: path });
        // It's possible that we end up with a row with only an empty text node,
        // don't try to remove it, it's important for the split calculations.
      });
    });

    Transforms.select(editor, Editor.start(editor, selectionOriginCellPath));
  }

  static isEnabled({ editor, controller }: ToolArgs): boolean {
    if (!editor?.selection) return false;
    const { tableSelection } = controller.state.pluginState;

    if (
      Range.isCollapsed(editor.selection) ||
      !tableSelection.tableRange ||
      !tableSelection.multipleCellsSelected
    ) {
      return false;
    }

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return false;

    return tableStructure.canMergeCells(tableSelection);
  }
}

export default ToolMergeCells;
